import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { apiService } from '../services/api'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAuth?: boolean
  redirectTo?: string
}

interface User {
  _id: string
  email: string
  username: string
  role: 'user' | 'admin'
  name: string
  isActive: boolean
  plan: string
  subscription?: {
    plan: 'starter' | 'growth' | 'scale' | 'enterprise'
    status: string
    [key: string]: any
  }
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
}) => {
  // Initialize auth state based on token existence
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(() => {
    const token = localStorage.getItem('token')
    return token ? true : null // If token exists, assume authenticated until proven otherwise
  })
  const location = useLocation()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        let token = localStorage.getItem('token')

        // 접근 토큰이 없어도 재발급 쿠키가 남아 있으면 세션을 되살린다.
        // 접근 토큰 수명이 15분이라, 이것이 없으면 잠깐 자리를 비운
        // 사이에도 로그인이 풀린 것처럼 보인다.
        if (!token) {
          const restored = await apiService.restoreSession()
          if (!restored) {
            setIsAuthenticated(false)
            return
          }
          token = localStorage.getItem('token')
        }

        if (!token) {
          setIsAuthenticated(false)
          return
        }

        // Verify token with backend
        const response = await apiService.client.get('/auth/verify-token', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.data.success) {
          setIsAuthenticated(true)

          // Store user data
          localStorage.setItem('user', JSON.stringify(response.data.data.user))
        } else {
          setIsAuthenticated(false)
          localStorage.removeItem('token')
          localStorage.removeItem('user')
        }
      } catch (error) {
        console.error('Auth verification failed:', error)
        setIsAuthenticated(false)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      }
    }

    checkAuth()

    // Listen for auth changes
    const handleAuthChange = (event: CustomEvent) => {
      setIsAuthenticated(event.detail.authenticated)
    }

    // Listen for storage changes to update auth state
    const handleStorageChange = () => {
      const token = localStorage.getItem('token')
      if (token) {
        checkAuth()
      } else {
        setIsAuthenticated(false)
      }
    }

    window.addEventListener('auth-change', handleAuthChange as never)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('auth-change', handleAuthChange as never)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Loading state - only show loading if we don't have a token at all
  if (isAuthenticated === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Verifying authentication...
          </p>
        </div>
      </div>
    )
  }

  // Check authentication requirement
  if (requireAuth && isAuthenticated === false) {
    // Store the current location for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // If user is authenticated but trying to access auth pages, redirect to dashboard
  if (
    !requireAuth &&
    isAuthenticated === true &&
    (location.pathname === '/login' ||
      location.pathname === '/register' ||
      location.pathname === '/verify-email')
  ) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export default ProtectedRoute

// Hook for using auth context
export const useAuth = () => {
  const [user, setUser] = useState<User | null>(() => {
    const userData = localStorage.getItem('user')
    if (userData) {
      try {
        return JSON.parse(userData)
      } catch (error) {
        console.error('Failed to parse user data:', error)
        localStorage.removeItem('user')
        return null
      }
    }
    return null
  })

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    return !!(token && userData)
  })

  useEffect(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData)
        setUser(parsedUser)
        setIsAuthenticated(true)
      } catch (error) {
        console.error('Failed to parse user data:', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        setUser(null)
        setIsAuthenticated(false)
      }
    } else {
      setUser(null)
      setIsAuthenticated(false)
    }
  }, [])

  const login = (token: string, userData: User) => {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(userData))
    setUser(userData)
    setIsAuthenticated(true)

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('auth-change', { detail: { authenticated: true } })
    )
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
    setIsAuthenticated(false)

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('auth-change', { detail: { authenticated: false } })
    )
  }

  return {
    user,
    isAuthenticated,
    login,
    logout,
    isAdmin: user?.role === 'admin',
  }
}
